import { Component, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { OverlayPanel } from 'primeng/overlaypanel';
import { Subscription } from 'rxjs';
import {
  NotificationItem,
  NotificationService,
  getNotificationTypeConfig,
} from 'src/app/services/notification.service';

@Component({
  selector: 'notification-bell',
  templateUrl: './notification-bell.component.html',
  styleUrls: ['./notification-bell.component.sass'],
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  @ViewChild('notifPanel') notifPanel: OverlayPanel;

  unreadCount = 0;
  notifications: NotificationItem[] = [];
  loading = false;
  onlyUnread = false;

  private unreadSub?: Subscription;
  private hasInteracted = false;
  private initialLoad = true;
  private readonly markInteracted = () => { this.hasInteracted = true; };

  constructor(
    private notificationService: NotificationService,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    // Sound requires a prior user gesture — capture the first one anywhere on the page.
    document.addEventListener('pointerdown', this.markInteracted, { once: true, capture: true });
    this.fetchUnreadCount();
    this.unreadSub = this.notificationService.connectUnreadCountStream().subscribe((count) => {
      this.ngZone.run(() => this.applyUnreadCount(count));
    });
  }

  ngOnDestroy(): void {
    this.unreadSub?.unsubscribe();
    document.removeEventListener('pointerdown', this.markInteracted, { capture: true });
  }

  async openPanel(event: Event): Promise<void> {
    if (this.notifPanel.overlayVisible) {
      this.notifPanel.hide();
      return;
    }
    this.notifPanel.show(event);
    await this.loadNotifications();
  }

  async loadNotifications(): Promise<void> {
    this.loading = true;
    try {
      const { data } = await this.notificationService.getNotifications(0, 10);
      this.notifications = data;
    } catch {
      // silently fail — list is non-critical
    } finally {
      this.loading = false;
    }
  }

  async fetchUnreadCount(): Promise<void> {
    try {
      const { count } = await this.notificationService.getUnreadCount();
      this.applyUnreadCount(count);
    } catch {
      // non-critical
    }
  }

  private applyUnreadCount(count: number): void {
    const previous = this.unreadCount;
    if (
      !this.initialLoad &&
      count > previous &&
      this.hasInteracted &&
      !this.notifPanel?.overlayVisible
    ) {
      this.playChime();
    }
    this.unreadCount = count;
    this.initialLoad = false;
    if (this.notifPanel?.overlayVisible && count !== previous) {
      void this.loadNotifications();
    }
  }

  private playChime(): void {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();

      const strike = (freq: number, gain: number, decay: number) => {
        const g = ctx.createGain();
        g.gain.setValueAtTime(gain, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + decay);
        g.connect(ctx.destination);

        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, ctx.currentTime);
        osc.connect(g);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + decay);
        osc.onended = () => ctx.close();
      };

      // B4 fundamental + harmonics — warm bell tone, volume capped at 0.25
      strike(494, 0.25, 1.4);
      strike(988, 0.10, 0.9);
      strike(1480, 0.04, 0.6);
    } catch {
      // AudioContext unavailable or suspended — silent fail
    }
  }

  async onNotificationClick(notification: NotificationItem): Promise<void> {
    if (notification.isRead) return;
    try {
      await this.notificationService.markAsRead(notification._id);
      notification.isRead = true;
      this.unreadCount = Math.max(0, this.unreadCount - 1);
    } catch {}
  }

  async onDismiss(notification: NotificationItem, event: Event): Promise<void> {
    event.stopPropagation();
    try {
      await this.notificationService.deleteNotification(notification._id);
      this.notifications = this.notifications.filter(
        (n) => n._id !== notification._id,
      );
      if (!notification.isRead) {
        this.unreadCount = Math.max(0, this.unreadCount - 1);
      }
    } catch {}
  }

  async onMarkAllAsRead(): Promise<void> {
    try {
      await this.notificationService.markAllAsRead();
      this.notifications.forEach((n) => (n.isRead = true));
      this.unreadCount = 0;
    } catch {}
  }

  get displayedNotifications(): NotificationItem[] {
    return this.onlyUnread
      ? this.notifications.filter((n) => !n.isRead)
      : this.notifications;
  }

  get badgeLabel(): string {
    return this.unreadCount > 99 ? '99+' : String(this.unreadCount);
  }

  get ariaLabel(): string {
    return this.unreadCount > 0
      ? `Notifications (${this.unreadCount} unread)`
      : 'Notifications';
  }

  typeConfig(type: string) {
    return getNotificationTypeConfig(type);
  }

  timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  }
}
